import { Search } from "lucide-react";
import { useMemo } from "react";
import { SeverityDot, severityTone } from "@/components/ui/severity-dot";
import { Timestamp } from "@/components/ui/timestamp";
import { cn } from "@/lib/utils";
import type { Severity } from "@/components/finding-badges";

export interface RouteItemData {
  routeKey: string;
  samplePageUrl: string | null;
  stepCount: number;
  lastVisitedAt: Date | string | null;
  problemCount: number;
  highestSeverity: string | null;
}

interface RouteListProps {
  routes: RouteItemData[];
  selectedRouteKey: string | null;
  onSelectRoute: (routeKey: string) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  className?: string;
}

export function RouteList({
  routes,
  selectedRouteKey,
  onSelectRoute,
  searchQuery,
  onSearchChange,
  className,
}: RouteListProps) {
  const filteredRoutes = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return routes;
    return routes.filter((r) => r.routeKey.toLowerCase().includes(q));
  }, [routes, searchQuery]);

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Filter routes…"
          className="h-8 w-full rounded-md border border-input bg-background pl-8 pr-3 text-xs placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        />
      </div>

      <div className="flex flex-col space-y-0.5 overflow-y-auto max-h-[calc(100vh-280px)] pr-1">
        {filteredRoutes.length === 0 ? (
          <div className="py-6 text-center text-xs text-muted-foreground">
            No routes matching “{searchQuery}”
          </div>
        ) : (
          filteredRoutes.map((route) => {
            const isSelected = route.routeKey === selectedRouteKey;
            const hasProblems = route.problemCount > 0;

            return (
              <button
                key={route.routeKey}
                type="button"
                onClick={() => onSelectRoute(route.routeKey)}
                className={cn(
                  "flex flex-col items-start gap-1 rounded-md px-2.5 py-2 text-left transition-colors cursor-pointer select-none outline-none",
                  "hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring",
                  isSelected
                    ? "bg-muted font-semibold text-foreground"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                <div className="flex w-full items-center justify-between gap-2">
                  <span
                    className={cn(
                      "truncate font-mono text-xs",
                      isSelected
                        ? "text-foreground font-semibold"
                        : "text-foreground font-medium",
                    )}
                    title={route.routeKey}
                  >
                    {route.routeKey}
                  </span>

                  {hasProblems ? (
                    <div className="flex items-center gap-1.5 shrink-0">
                      {route.highestSeverity && (
                        <SeverityDot
                          tone={severityTone(route.highestSeverity as Severity)}
                        />
                      )}
                      <span className="text-xs tabular-nums font-semibold text-destructive">
                        {route.problemCount}
                      </span>
                    </div>
                  ) : (
                    <span className="text-xs tabular-nums text-muted-foreground">
                      Clean
                    </span>
                  )}
                </div>

                <div className="flex w-full items-center justify-between text-xs text-muted-foreground">
                  <span className="tabular-nums">
                    {route.stepCount} step{route.stepCount === 1 ? "" : "s"}
                  </span>
                  {route.lastVisitedAt && (
                    <Timestamp
                      date={route.lastVisitedAt}
                      className="text-muted-foreground"
                    />
                  )}
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}
